<template>
  <ul class="logo-list" :class="['logo-list--' + tone, 'logo-list--' + size]">
    <li v-for="logo in logos" :key="logo.name" class="logo">
      <img
        v-if="!dropped.includes(logo.name)"
        class="logo-mark"
        :src="logo.src"
        alt=""
        decoding="async"
        @error="drop(logo.name)"
      />
      <span class="logo-name">{{ logo.name }}</span>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A mark with the name it is a mark for.

   One object, two scales. It existed once already, as the model strip on the AI
   page, and a case page's build row needed the same thing: an image and the word
   it stands for, in that order, with the word quiet. That is not a second
   pattern, so it is not a second component with its own type size, its own
   opacity and its own filter, which is what the two would have been a month
   later. `tone` and `size` are the same two axes `IconTile` already carries, and
   for the same reason.

   The name is never translated and never passed through `t()`. It is a product's
   own name, like the provider names on the AI page, and it stays visible while
   the mark is still on its way, so a row reads as intentional rather than as a
   gap with captions missing. The mark itself is decorative: the name is already
   the text, so the image carries no alt of its own.

   The name carrying the row is why a mark that has no file is dropped rather
   than left to fail: an `<img>` with nothing to load draws the browser's broken
   glyph beside the name, which is the one thing that reads as a mistake. The
   name is the text and the mark is the decoration, so an absent decoration
   removes itself and the row is a row of names, which is what the case page
   renders while the artwork is still arriving. It is the same answer the page
   gives an unsupplied capture and an unsupplied figure.

   Every mark is held to one colour until the pointer reaches it, and the mark the
   pointer is on is shown in whatever colour it actually is. A row of marks in
   their own brand colours is a row of nine brands competing with each other,
   which is the opposite of what the row is for: it is the content of a build, and
   it reads as one object when the marks share a single treatment and the names
   carry the meaning. The reveal is a reward on a row that is already legible, not
   a state the row depends on, so a reader who never points at it loses nothing.

   This is also why nothing here lifts or changes its edge under the pointer. A
   mark in a build row is a statement about the work, not a control, and one that
   lifts when it cannot be clicked teaches the pointer to expect a link that never
   arrives. The colour is the whole of the response, and it is a change of
   treatment rather than of position, so the row never moves as the pointer
   crosses it. */
.logo-list {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
}

.logo {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  text-align: center;
}

/* The mark itself, held to one colour that keeps its tones.

   `grayscale(1)` takes the hue out of every pixel and leaves its lightness alone,
   so the mark is one colour without being one flat shape: the two tones of the
   Vue glyph stay two tones, and a light glyph on a coloured ground stays a light
   glyph on a ground instead of turning into a solid block. `brightness(0)` was
   the first attempt at this row and it is the one thing the treatment cannot be,
   because it collapses every opaque pixel to the same ink. Any mark whose shape
   is carried by more than one tone flattened into a block under it, so a wordmark
   and a drawing arrived at the row looking identical.

   Alpha is untouched either way, so the mark's outline is still its own shape and
   its edges stay as smooth as the file's. There is deliberately no opacity on
   this ground. Grayscale is already the quiet step, because removing the hue is
   what stops nine brands competing, and a translucent mark on top of that would
   compress the tones back toward each other: the Vue navy and green measure about
   61 and 165 in lightness, and at 60% over white they come out at 138 and 201,
   which is two thirds of the difference the filter was chosen to keep.

   The transition covers the reveal below, so the colour comes back on the quick
   step every other treatment on the site uses. */
.logo-mark {
  filter: grayscale(1);
  object-fit: contain;
  transition: filter $dur $ease, opacity $dur $ease;
  width: auto;
}

/* The caption under a mark, one notch under the $label-* step every small
   uppercase label on the site is set on. The notch is what says this label is
   not doing the other labels' job: a kicker, a crumb trail or a group label
   introduces what follows it, and this one captions the object above it. On the
   build row it also stood at the exact size of the group label over the list
   ("Tech Stack"), so the group and the things in it were set the same and the
   list read as one flat block.

   The notch is taken out of the token rather than spelled, so the one owner of a
   small uppercase label's size still owns this one and a change to $label-size
   carries here. Weight and tracking are not a second decision and stay the
   token's: only the size steps down, in the one place this object names it. */
$logo-caption-step: 0.8;

.logo-name {
  font-family: $headings-font-family;
  font-size: $label-size * $logo-caption-step;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  text-transform: uppercase;
  white-space: nowrap;
}

/* The reference scale: a build row on a light band. The marks are read one at a
   time there, name under each, so they are set to be looked at rather than to
   caption the row.

   Every mark sits in one square tile, the same size for every entry, and the
   list is a grid of equal cells rather than a row of items sized by their own
   mark. That is the whole of this scale's job.

   It used to be the second of those, with a `max-width` of 10.5rem holding it
   together, and the cap was the problem. A width only ever binds a mark wider
   than the box is tall, so on this row a 1:1 glyph came out at 58px and a 2.4:1
   wordmark at 138px: one oversized wordmark standing among small glyphs, which
   is what the row looked like. A square bounds every mark by the same measure,
   so a square glyph and a wordmark are the same size as objects before their
   names are read, and `contain` keeps each mark's own proportions inside the
   tile instead of stretching one to fill it.

   The cells are equal because a grid track is, and that is what makes the row
   read in rows. With each item as wide as its own mark, two neighbours were
   pushed apart by whichever of them was wider, so no two gaps in the row were
   the same and the marks never lined up.

   The name wraps inside its cell rather than setting the cell's width. `nowrap`
   is right in the band scale below, where the marks are a texture and a caption
   must not reflow it; in a grid the caption already sits in a track that has a
   width, so a long product name reflows instead of pushing its track wider than
   its neighbours.

   Four to a row is the ceiling, and the tracks are what hold it. The minimum
   track is set to the width four of them and their gaps come to, so a fifth can
   never fit however wide the column gets, and the count falls below four on its
   own when the column is too narrow for it. Below that floor the minimum is the
   tile's own 4.4rem, so a phone fits as many as it has room for and the scale
   needs no breakpoint to do it.

   The ceiling is the tile's rather than the group's: the build row's first group
   holds seven marks and wraps to a second row either way, so what four decides is
   how large one tile is, not how many entries a group may have. Four in half the
   band is a tile of about 99px, which is the size at which the square reads as an
   object a mark is sitting on rather than as a chip with a glyph in it.

   The tile is the light ground's surface. This scale is only used on a light
   band, so it names no tone: a dark one would take the dark fill rather than
   this one, the way `IconTile`'s outline variant does.

   This sits within a step of the band scale below, and that is not an ordering
   claim: the two exist for different jobs rather than as two rungs, so a build
   row's marks being nearly the size of a marquee's is a consequence of both being
   read as images rather than a sign the scales have merged. */
$logo-track-max: 7.5rem;
$logo-gap: 0rem;

.logo-list--md {
  display: grid;
  gap: 1rem $logo-gap;
  grid-template-columns: repeat(auto-fill, minmax(max(4.4rem, calc((100% - #{$logo-gap * 3}) / 4)), 1fr));
  /* The cap the note above explains, written from the track and the gap rather
     than as the 30rem they multiply out to, so that moving either one moves
     this with it: a cap that disagreed with the tracks would leave the last
     column either short or overflowing. It is the track's measure and not the
     tile's because the tiles are narrower than their tracks now, so four tiles
     would no longer be four tracks. */
  max-width: $logo-track-max * 4 + $logo-gap * 3;

  .logo {
    gap: 0.7rem;
  }

  /* The tile, and the air the cell gives it. The tile is three quarters of its
     track and the cell's own centring splits what is left either side of it.

     That remainder is real and it is not free. A tile narrower than its track
     leaves the difference to the item's centring, so each column carries half of
     it on either side and the space the eye reads between two marks is the
     declared column gap plus that slack. The declared gap is zero here, so the
     whole of the space is slack: the tiles are held apart by the air their own
     width leaves rather than by the rule. That is the reason the gap is the
     weaker of the two levers on this row, and why taking it to zero moves the
     marks only a little, where widening the tile is what moves them properly. An
     earlier version of this note argued the opposite and removed the slack by
     filling the track, and that argument still stands: a drawn gap is not the
     same as a declared one. Only the drawn one is left here, and whether a
     declared gap comes back is a question about how much air the marks want
     rather than about how the row is built. If the tiles should sit on a declared
     gap again, the gap takes a value and the cap follows it on its own, because
     the cap is written from the two.

     The square is kept by `aspect-ratio` rather than by a height, because the
     track is what sets the width now.

     The list itself is capped at four tracks of 7.5rem, and that is what keeps the
     tile and its mark the same size when the group stops sharing the band. Above
     lg a group has half the band and the tracks land at about 99px on their own;
     below lg the plate is one column, so a group has the whole band, four tracks
     would come to about 171px at a tablet's measure, and the tile and the mark
     inside it would be half again the size they are on a desktop. The mark is the
     thing being read and it should not change size because the column got wider,
     so the tracks stop growing here.

     The cap is on the list rather than on the tile, and that is not a detail. A
     `max-width` on the tile would leave each one sitting in a track wider than
     itself, so the space the eye read between two tiles would be the declared gap
     plus the slack on both sides of both of them: the fixed-width tile this scale
     was rewritten to remove, arriving by another route. Capped here, the tracks
     are still the list's own and every gap is the one declared above.

     It is on the list rather than on the group for the same reason read from the
     other end. The plate's rules and each head's rule are drawn at the group's
     measure, so a group that stopped short of the band would leave those
     hairlines a different length from the band's own rules around them, and the
     cross that makes the four quarters one sheet would no longer line up with the
     card and the slab above it. The ruling stays the band's; only the tiles stop
     growing. The cost is the gap at the end of a row on a tablet, where the tiles
     stop before the head's tally does, and that is the side to take: a short row
     under a full-width rule reads as a list that ends, where a rule that stops
     halfway reads as a mistake.

     The padding is the tile's air, and the two percentages do not resolve against
     the same box. `width` is a fraction of the track; `padding` is also a fraction
     of the track, because a percentage padding always resolves against the
     containing block, and the containing block is the `.logo` cell, which fills
     the track. So `width: 75%` and `padding: 20%` are not the ratio they look
     like: the mark's content box comes to `75% - 2 x 20%`, a third of the track,
     and the air on each side is 20% of the track against a tile that is 75% of it,
     which is a little over a quarter of the tile. Writing the pair is what keeps
     that visible; writing "the mark keeps 35% of the track" would hide which of
     the two boxes it is a third of.

     A percentage is the only unit that does this job, because the box it insets is
     fluid. A rem inset is a constant number against every width, so the mark's
     share of the tile drifted with the column: at 1rem the mark filled about seven
     tenths of a 100px tile and about a third of the 70px one, which is two
     different objects wearing one rule. Resolved against the track, one share
     holds at every width, so the row reads the same shape at every column.

     The mark is smaller than the one this replaces, and the tile is smaller with
     it. The old pair held the mark at about 43px while the tile grew with the
     track, because a larger surface giving its mark more air was the point then;
     the owner has since asked for the marks themselves to come down, so both
     numbers move together here and the mark is about 35px at the lg measure. That
     also puts the air nearer its floor than it has been: the band is a quarter to
     a third of the tile, and 20% of the track is a little over a quarter of it, so
     the marks that are filled squares of their own - Illustrator's and Photoshop's
     near-black blocks - are as close to reading as framed blocks as they have
     been. That is the edge this pair sits on, and it is written down because a
     step further in either percentage crosses it.

     The floor came from the tile rather than from taste: a mark that is itself a
     filled square reads as a framed block rather than as a mark on a surface once
     the air around it drops below about a quarter of the tile, and it reads as a
     small mark on a large empty tile once the air passes about a third. 0.65rem
     was the first attempt here and it was well under that floor, which is why the
     row arrived as nine tiles of edge-to-edge ink.

     It carries no token, and that is the one thing this scale does not share with
     the caption below. The caption's size is a step down from `$label-size`, so
     it has to be written as that step to stay tied to it. This is a proportion of
     the track and nothing else on the site is, so a token for it would be a name
     for a number that is written once, in the only tile of this kind. */
  .logo-mark {
    aspect-ratio: 1;
    background: $ground;
    border: 1px solid $line-blue;
    border-radius: $r-md;
    padding: 20%;
    width: 75%;
  }

  .logo-name {
    white-space: normal;
  }
}

/* The band scale: one row of marks as the content of the strip, scrolled by
   Marquee. Here the marks are read together as a texture rather than one at a
   time, so the height is set by the band's own rhythm instead of by a single
   mark's legibility.

   The items space themselves with their own padding rather than with `gap` on
   the list, because the marquee's seam sits between two copies of this list:
   a gap is not paid across the join, so a gap-spaced loop touches itself and
   the seam becomes visible. */
.logo-list--lg {
  flex-wrap: nowrap;

  .logo {
    gap: 0.6rem;
    padding: 0 2rem;
  }

  .logo-mark {
    height: 3rem;
  }
}

/* The reveal. The mark the pointer is on is shown in whatever colour it actually
   is, and a dark ground also brings its opacity back up, which is the half a light
   ground has nothing to restore. It is the same on both grounds, which is why it
   is declared once here rather than inside either tone below: it carries no colour
   of its own, so there is nothing for a ground to change.

   Its specificity is what lets it win over the dark ground's ink without
   `!important`: the pseudo-class makes it a class heavier than the tone's own
   rule, so `filter: none` lands there as well as here. */
.logo:hover .logo-mark {
  filter: none;
  opacity: 1;
}

/* A light ground takes the label colour the site already uses for small uppercase
   text on white. The grayscale above is the whole treatment for that ground, so
   the tone has nothing of its own to say about the mark. */
.logo-list--light .logo-name {
  color: $label-on-light;
}

/* A dark ground flattens to one ink, and it is the ground that forces it rather
   than a second opinion about the treatment above.

   The marks on this ground are a mix of ends: anthropic, grok and openai are white
   files drawn for a dark surface, and meta and qwen are near-black. No filter
   that keeps their tones can keep both ends visible on ink. Grayscale alone leaves
   the white marks and loses the black ones, and inverting the grayscale does the
   opposite, so either way part of the row disappears. One ink is the only
   treatment that holds all of them, which is why `brightness(0)` stays here even
   though it is what the light ground just gave up.

   The opacity is this ground's own, and it is 0.6 for the reason the whole rule
   used to carry it: a row of white shapes at full strength is louder than a band
   wants to be. It is not on the shared rule because the light ground's tones are
   the point of its filter and transparency is what would take them away.

   $on-dark-muted is the step every other quiet label on a dark band uses. */
.logo-list--dark {
  .logo-mark {
    filter: brightness(0) invert(1);
    opacity: 0.6;
  }

  .logo-name {
    color: $on-dark-muted;
  }
}
</style>

<script>
export default {
  props: {
    /* The marks, as `{ name, src }`. The name is the product's own, so it is
       never translated; the path is the file the owner supplies. */
    logos: {
      type: Array,
      required: true
    },
    /* light | dark: which ground the row is sitting on. */
    tone: {
      type: String,
      default: 'light'
    },
    /* md: the reference scale, for a build row. lg: the band scale, for a strip
       whose marks are the content. */
    size: {
      type: String,
      default: 'md'
    }
  },

  data() {
    return {
      /* The names whose mark has no file. Held per render rather than per
         project, and cleared when the row is handed a different set of marks,
         so a name that failed on one case page is tried again on the next one
         instead of staying hidden for the rest of the session. */
      dropped: []
    };
  },

  watch: {
    logos() {
      this.dropped = [];
    }
  },

  methods: {
    drop(name) {
      if (!this.dropped.includes(name)) {
        this.dropped.push(name);
      }
    }
  }
};
</script>
